import type { FieldContext } from 'vee-validate';
import { computed, type MaybeRefOrGetter, toValue } from 'vue';

import type { HTMLElementEvent } from '@/kasten-components/types/html-element-types';
import { noop } from '@/kasten-components/utils';

export type ValidationInteractionMode =
  'passive' | 'passive-aggressive' | 'lazy' | 'eager' | 'aggressive';

export type ValidationEventHandlers<T = HTMLElementEvent> = {
  onBlur: (event: FocusEvent) => void;
  onInput: (payload: T) => void;
  onChange: (payload: T) => void;
};

export type CustomComponentEventHandlers<T = HTMLElementEvent> = Partial<
  ValidationEventHandlers<T>
>;

export const useInteractionMode = <T = HTMLElementEvent>(
  mode: MaybeRefOrGetter<ValidationInteractionMode>,
  fieldCtx: FieldContext,
  { onInput = noop, onBlur = noop, onChange = noop }: CustomComponentEventHandlers<T> = {},
) => {
  const { handleBlur, handleChange, validate, errorMessage } = fieldCtx;

  const hasError = computed(() => toValue(errorMessage) !== undefined);

  const validationModeMap: Record<ValidationInteractionMode, ValidationEventHandlers<T>> = {
    passive: {
      onBlur: (event) => {
        handleBlur(event);
        onBlur(event);
      },

      onInput: (event) => {
        handleChange(event, false);
        onInput(event);
      },

      onChange: (event) => {
        handleChange(event, false);
        onChange(event);
      },
    },

    ['passive-aggressive']: {
      onBlur: (event) => {
        handleBlur(event);
        onBlur(event);
      },

      onInput: (event) => {
        handleChange(event, hasError.value);
        onInput(event);
      },

      onChange: (event) => {
        handleChange(event, hasError.value);
        onChange(event);
      },
    },

    lazy: {
      onBlur: (event) => {
        handleBlur(event);
        onBlur(event);
      },

      onInput: (event) => {
        handleChange(event, false);
        onInput(event);
      },

      onChange: (event) => {
        handleChange(event, true);
        onChange(event);
      },
    },

    eager: {
      onBlur: (event) => {
        handleBlur(event);
        if (!hasError.value) validate();
        onBlur(event);
      },

      onInput: (event) => {
        handleChange(event, hasError.value);
        onInput(event);
      },

      onChange: (event) => {
        handleChange(event, true);
        onChange(event);
      },
    },

    aggressive: {
      onBlur: (event) => {
        handleBlur(event);
        onBlur(event);
      },

      onInput: (event) => {
        handleChange(event, true);
        onInput(event);
      },

      onChange: (event) => {
        handleChange(event, true);
        onChange(event);
      },
    },
  };

  return computed(() => validationModeMap[toValue(mode)]);
};
